import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  useTheme,
  Chip
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  School as SchoolIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  CallSplit as CallSplitIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  MergeType as MergeTypeIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../../../auth/AuthContext';

import SchoolReprocessor from './SchoolReprocessor';
import EnhancedStandardProcessor from './EnhancedStandardProcessor';
import SplitSchoolWorkflow from './SplitSchoolWorkflow';
import NewSchoolWorkflow from './NewSchoolWorkflow';
import NonNCESProcessingWorkflow from './NonNCESProcessingWorkflow';
import UpgradeDropAPinProcessor from './UpgradeDropAPinProcessor';
import MergeSchoolWorkflow from './MergeSchoolWorkflow';
import BoundaryConstrainedProcessor from './BoundaryConstrainedProcessor';



const WorkflowTester = ({ selectedWorkflow, onWorkflowSelect }) => {
  const theme = useTheme();
  const { user } = useAuth();

  const baseWorkflows = [
    { 
      key: 'STANDARD', 
      label: 'Standard NCES Processing', 
      icon: <SchoolIcon />, 
      color: 'primary',
      description: 'Process existing NCES schools through all Cloud Run services'
    },
    { 
      key: 'REPROCESS', 
      label: 'Reprocessing', 
      icon: <SettingsIcon />, 
      color: 'secondary',
      description: 'Reprocess existing schools with updated data'
    },
    { 
      key: 'NEW_SCHOOL', 
      label: 'Drop a Pin', 
      icon: <AddIcon />, 
      color: 'success',
      description: 'Drop a pin at any location to create a new data point with address, team assignment, and data collection'
    },
    { 
      key: 'NON_NCES_PROCESSING', 
      label: 'Non-NCES Processing', 
      icon: <SchoolIcon />, 
      color: 'info',
      description: 'Process schools without NCES IDs that have enrollment data from 2017-2025'
    },
    { 
      key: 'SPLIT_SCHOOL', 
      label: 'School Splits', 
      icon: <CallSplitIcon />, 
      color: 'warning',
      description: 'Split existing schools into components'
    },
    { 
      key: 'UPGRADE_DROP_A_PIN', 
      label: 'Upgrade Drop-a-Pins', 
      icon: <TrendingUpIcon />, 
      color: 'success',
      description: 'Import enrollment data from Excel to upgrade drop-a-pin locations to full schools with projections'
    },
    {
      key: 'MERGE_SCHOOLS',
      label: 'Merge Schools',
      icon: <MergeTypeIcon />,
      color: 'error',
      description: 'Consolidate two schools with different NCES IDs into one unified record with complete enrollment history'
    },
    {
      key: 'BOUNDARY_CONSTRAINED',
      label: 'Boundary-Constrained Polygons',
      icon: <MapIcon />,
      color: 'info',
      description: 'Clip a drive-time ring to a state, county, or school district boundary, then re-run ESRI, nearby schools, metrics, and AI summaries for that study area'
    }
  ];

  const workflows = baseWorkflows;

  const renderWorkflowContent = () => {
    switch (selectedWorkflow) {
      case 'STANDARD':
        return <EnhancedStandardProcessor />;
      case 'REPROCESS':
        return <SchoolReprocessor />;
      case 'NEW_SCHOOL':
        return <NewSchoolWorkflow />;
      case 'NON_NCES_PROCESSING':
        return <NonNCESProcessingWorkflow />;
      case 'SPLIT_SCHOOL':
        return <SplitSchoolWorkflow />;
      case 'UPGRADE_DROP_A_PIN':
        return <UpgradeDropAPinProcessor />;
      case 'MERGE_SCHOOLS':
        return <MergeSchoolWorkflow />;
      case 'BOUNDARY_CONSTRAINED':
        return <BoundaryConstrainedProcessor />;
      default:
        return (
          <Box>
            {/* Workflow Dashboard */}
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              School Processing Workflows
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Choose a workflow type to begin processing schools through the Cloud Run microservices architecture.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
              {workflows.map((workflow) => (
                <Card 
                  key={workflow.key}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => onWorkflowSelect(workflow.key)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        bgcolor: `${workflow.color}.50`,
                        color: `${workflow.color}.main`,
                        mr: 2 
                      }}>
                        {workflow.icon}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {workflow.label}
                        </Typography>

                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {workflow.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>


          </Box>
        );
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          color: theme.palette.primary.main 
        }}>
          <AccountTreeIcon fontSize="large" />
          School Processing Workflows
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Orchestrate complete school processing workflows using Cloud Run microservices
        </Typography>
        
        {/* Authentication Status */}
        <Box sx={{ mt: 2 }}>
          <Chip
            icon={user ? <CheckIcon /> : <ErrorIcon />}
            label={user ? `Authenticated as ${user.email}` : 'Not authenticated'}
            color={user ? 'success' : 'error'}
            variant="outlined"
          />
        </Box>
      </Box>


      {/* Main Content */}
      {renderWorkflowContent()}
    </Container>
  );
};

export default WorkflowTester; 